import React, { useMemo, useState } from 'react'
import { fmtDuration, jobFinancials, jobPaymentMeta, jobServiceSummary, jobStatusMeta } from '../lib/utils'

function safeDate(value) {
  if (!value) return null
  const date = new Date(value)
  return Number.isNaN(date.getTime()) ? null : date
}

function fmtDateTime(value) {
  const date = safeDate(value)
  if (!date) return ''
  return date.toLocaleString('en-GB', { dateStyle: 'medium', timeStyle: 'short' })
}

function money(value) {
  const amount = Number(value || 0)
  return amount > 0 ? `GBP ${amount.toFixed(2)}` : ''
}

function pushEvent(events, event) {
  const at = safeDate(event.at)
  if (!at || !event.title) return
  events.push({
    ...event,
    at: at.toISOString(),
    tone: event.tone || 'muted',
  })
}

export function buildJobTimeline({ job = {}, customer, component, evidenceItems = [], reportVersions = [] }) {
  const events = []
  const financials = jobFinancials(job)
  const customerName = customer ? `${customer.first_name || ''} ${customer.last_name || ''}`.trim() : ''
  const componentName = component?.model
    ? `${component.year ? `${component.year} ` : ''}${component.model}`
    : ''

  if (job.booking_request_id) {
    pushEvent(events, {
      key: 'booking-accepted',
      at: job.created_at || job.updated_at || job.date,
      title: 'Booking accepted',
      detail: customerName ? `Created job for ${customerName}` : 'Booking request converted into a job.',
      tone: 'green',
    })
  } else {
    pushEvent(events, {
      key: 'job-created',
      at: job.created_at || job.date,
      title: 'Job created',
      detail: [customerName, componentName].filter(Boolean).join(' - '),
      tone: 'blue',
    })
  }

  if (job.date || job.created_at) {
    pushEvent(events, {
      key: 'job-received',
      at: job.created_at || job.date,
      title: 'Job received',
      detail: job.date ? `Received ${new Date(`${job.date}T12:00:00`).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}` : 'Job record created.',
      tone: 'muted',
    })
  }

  if (job.scheduled_date) {
    pushEvent(events, {
      key: 'job-scheduled',
      at: job.scheduled_date,
      title: 'Job scheduled',
      detail: `Booked in for ${new Date(`${job.scheduled_date}T12:00:00`).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}`,
      tone: 'blue',
    })
  }

  if (job.inspection_completed_at) {
    pushEvent(events, {
      key: 'inspection-completed',
      at: job.inspection_completed_at,
      title: 'Inspection completed',
      detail: job.inspection_summary || job.condition_notes || 'Initial inspection signed off.',
      tone: 'green',
    })
  }

  if (job.estimate_sent_at || job.quote_last_sent_at) {
    const quoteValue = money(job.estimate_amount)
    const baseValue = money(financials.serviceCost)
    pushEvent(events, {
      key: 'quote-sent',
      at: job.estimate_sent_at || job.quote_last_sent_at,
      title: 'Quote sent',
      detail: [quoteValue ? `Additional quote ${quoteValue}` : '', baseValue ? `Base service ${baseValue}` : '', job.estimate_notes].filter(Boolean).join(' - '),
      tone: 'orange',
    })
  }

  if (job.customer_decision_at || job.customer_decision) {
    const approved = String(job.customer_decision || '').toLowerCase() === 'approved'
    const declined = String(job.customer_decision || '').toLowerCase() === 'declined'
    pushEvent(events, {
      key: 'customer-decision',
      at: job.customer_decision_at || job.updated_at || job.estimate_sent_at,
      title: approved ? 'Customer approved quote' : declined ? 'Customer declined quote' : 'Customer response recorded',
      detail: job.customer_decision_note || (approved ? 'Workshop can continue.' : declined ? 'Quote declined.' : ''),
      tone: approved ? 'green' : declined ? 'orange' : 'muted',
    })
  }

  evidenceItems.forEach((item, index) => {
    pushEvent(events, {
      key: `evidence-${item.id || index}`,
      at: item.created_at,
      title: 'Evidence added',
      detail: [item.caption || item.file_name || 'File uploaded', item.stage, item.visibility === 'customer' ? 'customer visible' : 'internal'].filter(Boolean).join(' - '),
      tone: item.visibility === 'customer' ? 'green' : 'muted',
    })
  })

  if (job.qc_signed_off_at) {
    const failed = String(job.qc_status || '').toLowerCase() === 'failed'
    pushEvent(events, {
      key: 'qc-signed',
      at: job.qc_signed_off_at,
      title: failed ? 'QC failed' : 'QC signed off',
      detail: [job.qc_signed_off_by_name, job.qc_notes].filter(Boolean).join(' - '),
      tone: failed ? 'orange' : 'green',
    })
  }

  reportVersions.forEach(version => {
    pushEvent(events, {
      key: `report-version-${version.id}`,
      at: version.sent_at || version.created_at,
      title: version.delivery_status === 'sent' ? 'Report emailed' : 'Report PDF saved',
      detail: [
        version.version_number ? `Version ${version.version_number}` : '',
        version.recipient_email ? `to ${version.recipient_email}` : '',
        version.total_charged ? money(version.total_charged) : '',
      ].filter(Boolean).join(' - '),
      tone: 'green',
    })
  })

  if (job.report_sent_at && !reportVersions.some(version => version.delivery_status === 'sent')) {
    pushEvent(events, {
      key: 'report-sent',
      at: job.report_sent_at,
      title: 'Report sent',
      detail: 'Customer service report emailed.',
      tone: 'green',
    })
  }

  if (job.payment_requested_at || job.payment_link) {
    pushEvent(events, {
      key: 'payment-requested',
      at: job.payment_requested_at || job.updated_at || job.report_sent_at,
      title: 'Payment requested',
      detail: money(financials.totalCost) || jobPaymentMeta(job).label,
      tone: 'orange',
    })
  }

  if (job.payment_reminder_sent_at) {
    pushEvent(events, {
      key: 'payment-reminder',
      at: job.payment_reminder_sent_at,
      title: 'Payment reminder sent',
      detail: money(financials.totalCost),
      tone: 'orange',
    })
  }

  if (job.payment_paid_at) {
    pushEvent(events, {
      key: 'payment-paid',
      at: job.payment_paid_at,
      title: 'Payment paid',
      detail: [money(financials.totalCost), job.payment_method, job.payment_reference].filter(Boolean).join(' - '),
      tone: 'green',
    })
  }

  if (job.ready_for_collection_at) {
    pushEvent(events, {
      key: 'ready-collection',
      at: job.ready_for_collection_at,
      title: 'Ready for collection',
      detail: 'Job marked ready for customer handover.',
      tone: 'green',
    })
  }

  if (job.collected_at) {
    pushEvent(events, {
      key: 'collected',
      at: job.collected_at,
      title: 'Collected',
      detail: 'Customer collected the item.',
      tone: 'green',
    })
  }

  if (job.closed_at || String(job.status || '').toLowerCase() === 'completed') {
    const administrative = Boolean(job.administrative_closed_at)
    pushEvent(events, {
      key: 'closed',
      at: job.administrative_closed_at || job.closed_at || job.updated_at || job.collected_at,
      title: administrative ? 'Job administratively closed' : 'Job closed',
      detail: administrative
        ? [job.administrative_closed_by_name ? `Closed by ${job.administrative_closed_by_name}` : '', job.administrative_close_reason].filter(Boolean).join(' - ')
        : job.closed_by_name ? `Closed by ${job.closed_by_name}` : 'Service job completed.',
      tone: administrative ? 'orange' : 'green',
    })
  }

  if (String(job.status || '').toLowerCase() === 'cancelled') {
    pushEvent(events, {
      key: 'cancelled',
      at: job.updated_at || job.customer_decision_at || job.created_at,
      title: 'Job cancelled',
      detail: job.customer_decision_note || 'No further action.',
      tone: 'muted',
    })
  }

  return events
    .sort((a, b) => new Date(a.at) - new Date(b.at))
    .filter((event, index, list) => index === list.findIndex(other => other.key === event.key))
}

export function jobNextAction(job = {}) {
  const status = jobStatusMeta(job)
  const payment = jobPaymentMeta(job)
  const serviceSummary = jobServiceSummary(job)

  if (job.administrative_closed_at) return { title: 'Administratively closed', detail: 'Required stages remain recorded as incomplete.', tone: 'orange' }
  if (String(job.status || '') === 'completed') return { title: 'Job complete', detail: job.closed_at ? `Closed ${fmtDateTime(job.closed_at)}` : 'Service lifecycle complete.', tone: 'green' }

  if (!job.customer_id) return { title: 'Add customer', detail: 'Link this job to a customer before continuing.', tone: 'orange' }
  if (!job.component_id) return { title: 'Create or assign component', detail: 'The job can run, but the service history needs a component record.', tone: 'orange' }
  if (!serviceSummary) return { title: 'Choose service', detail: 'Select the base service so the job has a clear scope and price.', tone: 'orange' }
  if (String(job.status || '') === 'awaiting_customer_item') return { title: status.label, detail: status.nextAction, tone: status.tone }
  if (!job.inspection_completed_at) return { title: 'Complete inspection', detail: 'Record intake condition and sign off the inspection section.', tone: 'orange' }
  if (String(job.customer_decision || '').toLowerCase() === 'pending') return { title: 'Wait for quote approval', detail: 'The customer has the approval link. Continue when approved.', tone: 'orange' }
  if (String(job.customer_decision || '').toLowerCase() === 'declined') return { title: 'Quote declined', detail: 'Confirm whether to cancel, revise or continue with base work only.', tone: 'orange' }
  if (!job.qc_signed_off_at) return { title: 'Complete service and QC', detail: 'Finish the service log, then pass or fail final QC.', tone: 'orange' }
  if (!job.report_sent_at) return { title: 'Send customer report', detail: 'QC is signed off. Save or email the service report next.', tone: 'green' }
  if (payment.key === 'not_requested') return { title: 'Request or record payment', detail: 'Use a payment link, bank transfer or manual payment record before handover.', tone: 'orange' }
  if (payment.key === 'requested') return { title: 'Waiting for payment', detail: 'Send a reminder or mark paid manually if needed.', tone: 'orange' }
  if (String(job.status || '') === 'ready_for_collection') return { title: 'Complete handover', detail: 'Payment is done. Close the job when collected.', tone: 'green' }
  return { title: status.label, detail: status.nextAction, tone: status.tone }
}

export default function JobTimeline({ job, customer, component, evidenceItems = [], reportVersions = [], compact = false }) {
  const [expanded, setExpanded] = useState(false)
  const events = useMemo(
    () => buildJobTimeline({ job, customer, component, evidenceItems, reportVersions }),
    [job, customer, component, evidenceItems, reportVersions]
  )
  const visibleEvents = compact && !expanded ? events.slice(-5) : events
  const next = jobNextAction(job)

  return (
    <div style={{ background: 'var(--black)', border: '1px solid var(--border)', borderRadius: 6, padding: 14 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, alignItems: 'flex-start', marginBottom: 12, flexWrap: 'wrap' }}>
        <div>
          <div className="fs11 text-muted fw7" style={{ textTransform: 'uppercase', letterSpacing: '.08em', marginBottom: 4 }}>Job timeline</div>
          <div className="fs12 text-muted">Next action: <span className="text-white fw7">{next.title}</span> - {next.detail}</div>
        </div>
        {job.duration_mins ? (
          <span className="job-status-chip job-status-muted">Duration {fmtDuration(job.duration_mins)}</span>
        ) : null}
      </div>

      {events.length ? (
        <div style={{ display: 'grid', gap: 10 }}>
          {visibleEvents.map((event, index) => (
            <div key={event.key} style={{ display: 'grid', gridTemplateColumns: '18px 1fr', gap: 10 }}>
              <div style={{ position: 'relative', minHeight: 36 }}>
                <span style={{
                  display: 'block',
                  width: 10,
                  height: 10,
                  borderRadius: 999,
                  marginTop: 4,
                  background: event.tone === 'green' ? 'var(--green)' : event.tone === 'orange' ? 'var(--yellow)' : event.tone === 'blue' ? 'var(--blue)' : 'var(--muted)',
                  boxShadow: '0 0 0 3px rgba(255,255,255,.06)',
                }} />
                {index < visibleEvents.length - 1 && (
                  <span style={{ position: 'absolute', top: 18, left: 4, bottom: -9, width: 1, background: 'var(--border)' }} />
                )}
              </div>
              <div>
                <div style={{ display: 'flex', justifyContent: 'space-between', gap: 10, flexWrap: 'wrap' }}>
                  <div className="text-white fw7 fs12">{event.title}</div>
                  <div className="text-muted fs11">{fmtDateTime(event.at)}</div>
                </div>
                {event.detail ? <div className="text-muted fs12" style={{ marginTop: 2, lineHeight: 1.45 }}>{event.detail}</div> : null}
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="fs12 text-muted">No timeline activity recorded yet.</div>
      )}

      {compact && events.length > visibleEvents.length && (
        <button className="btn btn-ghost btn-sm" style={{ marginTop: 12 }} onClick={() => setExpanded(true)}>
          Show full timeline
        </button>
      )}
      {compact && expanded && events.length > 5 && (
        <button className="btn btn-ghost btn-sm" style={{ marginTop: 12 }} onClick={() => setExpanded(false)}>
          Show latest only
        </button>
      )}
    </div>
  )
}
